import type { RoleplayCard } from '@wundo/shared/roleplay.ts';
import {
  formatDayAndMonth,
  formatShortDayMonthAndTime,
} from '@/components/agenda/meetingFormat.ts';
import type { AttemptTrend } from './attemptTrend.ts';
import { m } from '@/paraglide/messages.js';

/**
 * Who you are practising with, on the collapsed row.
 *
 * A library card names the character, because that is the only thing that
 * distinguishes two conversations of the same type. One made from a chat names
 * where it came from instead: the character is somebody the user described
 * minutes ago, and the day they did is what they will recognise.
 */
export function formatRoleplayMeta(card: RoleplayCard): string {
  const day = formatDayAndMonth(new Date(card.created_at));

  if (card.source === 'chat') return m.practice_meta_from_chat({ date: day });
  if (card.source === 'prep') return m.practice_meta_from_prep({ date: day });

  return `${card.persona.name}, ${card.persona.role}`;
}

/**
 * How often this was practised, and when last.
 *
 * "Nog niet geoefend" rather than a blank: the line is the reason to open a
 * card again, and an empty one reads as a row that failed to load.
 */
export function formatPracticeHistory(card: RoleplayCard): string {
  if (card.attempt_count === 0 || card.last_practiced_at === null) {
    return m.practice_history_none();
  }

  return m.practice_history({
    count: card.attempt_count,
    date: formatDayAndMonth(new Date(card.last_practiced_at)),
  });
}

/**
 * How long the conversation has been running, as mm:ss.
 *
 * Minutes keep counting past an hour rather than rolling over into a third
 * field: a practice conversation that long is already far past the point where
 * the clock is telling the user anything, and "01:04:11" reads as a recording.
 */
export function formatElapsed(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;

  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

/** The date an attempt is known by in the trend line. */
export function formatAttemptLabel(isoTimestamp: string): string {
  return formatDayAndMonth(new Date(isoTimestamp));
}

/**
 * An attempt in the picker: its number and the minute it ended, because
 * several attempts on one day are only told apart by the time.
 */
export function formatAttemptOption(
  number: number,
  isoTimestamp: string,
  latest: boolean,
): string {
  const date = formatShortDayMonthAndTime(new Date(isoTimestamp));
  return latest
    ? m.practice_attempt_option_latest({ number, date })
    : m.practice_attempt_option({ number, date });
}

/**
 * The one line over the feedback: how this attempt scored, and whether that is
 * movement. Against the previous attempt only — a running average over every
 * attempt hides the very thing somebody opens this for.
 */
export function formatTrend(
  trend: AttemptTrend,
  previousLabel: string | null,
): string | null {
  if (trend.average === null) return null;

  const average = trend.average.toFixed(1);
  if (trend.comparison === 'first' || previousLabel === null) {
    return m.practice_trend_first({ average });
  }

  if (trend.comparison === 'better') {
    return m.practice_trend_better({ average, previous: previousLabel });
  }
  if (trend.comparison === 'lower') {
    return m.practice_trend_lower({ average, previous: previousLabel });
  }
  return m.practice_trend_equal({ average, previous: previousLabel });
}
